import { useCallback, useEffect, useRef, useState } from 'react'
import type { ToolCallEvent } from '@/hooks/workspace/useWorkspaceSocket'
import type { ReportViewWithVersions } from '@/lib/types/report'
import { LIVE_STATUSES } from '@/lib/report/liveStatus'

const POLL_MS = 4000
const REPORT_TOOLS = new Set(['create_report', 'write_section', 'finalize_report'])

interface UseReportLiveArgs {
  fetchView: () => Promise<ReportViewWithVersions>
  reportId?: string | null
  events?: ToolCallEvent[]
}

export function useReportLive({ fetchView, reportId, events }: UseReportLiveArgs) {
  const [view, setView] = useState<ReportViewWithVersions | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [writingSection, setWritingSection] = useState<string | null>(null)
  const fetchingRef = useRef(false)
  const seenEventsRef = useRef(new Set<string>())

  const refresh = useCallback(async () => {
    if (fetchingRef.current) return
    fetchingRef.current = true
    try {
      setView(await fetchView())
      setError(null)
    } catch {
      setError('Report not found or not accessible.')
    } finally {
      fetchingRef.current = false
    }
  }, [fetchView])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const isLive = view != null && LIVE_STATUSES.has(view.status)

  useEffect(() => {
    if (!isLive) return
    const id = setInterval(() => void refresh(), POLL_MS)
    return () => clearInterval(id)
  }, [isLive, refresh])

  useEffect(() => {
    if (!events || !reportId) return
    // Events array is newest-first (socket prepends); iterate in reverse to process chronologically
    const freshEvents: typeof events = []
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i]
      const eventKey = `${e.call_id}:${e.data.status}`
      if (!seenEventsRef.current.has(eventKey)) {
        seenEventsRef.current.add(eventKey)
        freshEvents.push(e)
      }
    }
    for (const e of freshEvents) {
      if (e.type !== 'tool_call' || !REPORT_TOOLS.has(e.data.tool_name)) continue
      if (e.data.params?.report_id !== reportId) continue
      if (e.data.status === 'started' && e.data.tool_name === 'write_section') {
        setWritingSection((e.data.params?.section_id as string) ?? null)
      } else if (e.data.status === 'completed' || e.data.status === 'failed') {
        setWritingSection(null)
        void refresh()
      }
    }
  }, [events, reportId, refresh])

  return { view, error, writingSection, isLive, refresh }
}
